import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Edit,
  FlaskConical,
  Loader2,
  MinusCircle,
  SkipForward,
  XCircle,
} from 'lucide-react';
import { useToast } from '../components/Toast';
import {
  getInstance,
  INSTANCE_STATUS_BADGE,
  StepResultStatus,
  TEST_ACTORS,
  TEST_MODULES,
  TestCaseInstance,
} from '../services/testCasesApi';

// ── Constants ──────────────────────────────────────────────────────────────────

const stepStatusIcon: Record<StepResultStatus, React.ReactNode> = {
  passed: <CheckCircle2 className="w-4 h-4 text-green-600" />,
  failed: <XCircle className="w-4 h-4 text-red-600" />,
  skipped: <SkipForward className="w-4 h-4 text-gray-400" />,
  not_run: <MinusCircle className="w-4 h-4 text-gray-300" />,
};

const stepStatusLabel: Record<StepResultStatus, string> = {
  passed: 'Réussi',
  failed: 'Échoué',
  skipped: 'Ignoré',
  not_run: 'Non testé',
};

const stepStatusRowBg: Record<StepResultStatus, string> = {
  passed: 'bg-green-50 border-green-200',
  failed: 'bg-red-50 border-red-200',
  skipped: 'bg-gray-50 border-gray-200',
  not_run: 'bg-white border-gray-100',
};

const instanceStatusLabel: Record<string, string> = {
  NOT_RUN: 'Non exécuté',
  IN_PROGRESS: 'En cours',
  PASSED: 'Réussi',
  FAILED: 'Échoué',
  PARTIAL: 'Partiel',
  BLOCKED: 'Bloqué',
};

const severityLabel: Record<string, string> = {
  blocker: 'Bloquant',
  major: 'Majeur',
  minor: 'Mineur',
  cosmetic: 'Cosmétique',
};

const severityColor: Record<string, string> = {
  blocker: 'bg-red-100 text-red-800',
  major: 'bg-orange-100 text-orange-800',
  minor: 'bg-yellow-100 text-yellow-800',
  cosmetic: 'bg-gray-100 text-gray-700',
};

const ENV_LABEL: Record<string, string> = {
  LOCAL: 'Local',
  STAGING: 'Staging',
  PRODUCTION: 'Production',
};

// ── Component ──────────────────────────────────────────────────────────────────

export function TestCaseInstanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const [instance, setInstance] = useState<TestCaseInstance | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const data = await getInstance(id);
        setInstance(data);
      } catch (err: any) {
        showToast(err.message || 'Erreur de chargement', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!instance) {
    return <div className="text-center py-20 text-gray-500">Exécution introuvable.</div>;
  }

  const tc = instance.testCase;
  const moduleName = tc ? TEST_MODULES.find((m) => m.id === tc.moduleId)?.label ?? tc.moduleId : '';
  const actorName = tc ? TEST_ACTORS.find((a) => a.id === tc.primaryActor)?.label ?? tc.primaryActor : '';
  const executorLabel = instance.executorRole
    ? TEST_ACTORS.find((a) => a.id === instance.executorRole)?.label ?? instance.executorRole
    : null;

  const passed = instance.stepResults.filter((sr) => sr.status === 'passed').length;
  const failed = instance.stepResults.filter((sr) => sr.status === 'failed').length;
  const skipped = instance.stepResults.filter((sr) => sr.status === 'skipped').length;
  const total = instance.stepResults.length;
  const passRate = total > 0 ? Math.round((passed / total) * 100) : null;

  return (
    <div className="space-y-4">
      <ToastComponent />

      {/* ── Header bar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-4 py-3">
        <button
          onClick={() => navigate('/test-case-instances')}
          className="p-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0"
          title="Retour aux exécutions"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
            <span
              className="hover:text-amber-600 cursor-pointer"
              onClick={() => navigate('/test-case-instances')}
            >
              Exécutions
            </span>
            {tc && (
              <>
                <span>/</span>
                <span
                  className="hover:text-amber-600 cursor-pointer"
                  onClick={() => navigate(`/test-cases/${tc.id}`)}
                >
                  {tc.name}
                </span>
              </>
            )}
          </div>
          <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2 truncate">
            <FlaskConical className="w-4 h-4 text-amber-500 flex-shrink-0" />
            Résultat d'exécution
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${INSTANCE_STATUS_BADGE[instance.status]}`}>
              {instanceStatusLabel[instance.status]}
            </span>
          </h1>
        </div>

        <button
          onClick={() => navigate(`/test-case-instances/${id}/edit`)}
          className="btn-secondary inline-flex items-center gap-2 text-sm flex-shrink-0"
        >
          <Edit className="w-4 h-4" />
          Modifier
        </button>
      </div>

      {/* ── Session info strip ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        {/* Module */}
        {moduleName && (
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">Module</p>
            <p className="text-sm font-medium text-gray-800 truncate">{moduleName}</p>
          </div>
        )}

        {/* Actor */}
        {actorName && (
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">Acteur</p>
            <p className="text-sm font-medium text-gray-800 truncate">{actorName}</p>
          </div>
        )}

        {/* Tester */}
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">Testeur</p>
          <p className="text-sm font-medium text-gray-800 truncate">{instance.executorName || '—'}</p>
          {executorLabel && <p className="text-xs text-gray-400 truncate">{executorLabel}</p>}
        </div>

        {/* Environment */}
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">Environnement</p>
          <p className="text-sm font-medium text-gray-800">{ENV_LABEL[instance.environment] ?? instance.environment}</p>
        </div>

        {/* Date */}
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">Date</p>
          <p className="text-sm font-medium text-gray-800">
            {instance.date
              ? new Date(instance.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
              : '—'}
          </p>
          {(instance.startTime || instance.endTime) && (
            <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
              <Clock className="w-3 h-3" />
              {instance.startTime}{instance.endTime ? ` → ${instance.endTime}` : ''}
            </p>
          )}
        </div>

        {/* Pass rate */}
        {total > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">Résultats</p>
            <p className="text-sm font-bold text-gray-800">
              {passRate !== null ? `${passRate}%` : '—'}
            </p>
            <p className="text-xs text-gray-400">
              {passed}✓ {failed > 0 && <span className="text-red-500">{failed}✗</span>} {skipped > 0 && `${skipped} ignoré`} / {total}
            </p>
          </div>
        )}
      </div>

      {/* ── Main 2-col body ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

        {/* Left — step results (2/3) */}
        <div className="xl:col-span-2 space-y-4">

          {/* Progress bar */}
          {total > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>{passed} / {total} étapes réussies</span>
                <span>{failed} échec{failed > 1 ? 's' : ''}</span>
              </div>
              <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden flex gap-0.5">
                {instance.stepResults.map((sr, i) => (
                  <div
                    key={i}
                    className={`flex-1 rounded-sm ${
                      sr.status === 'passed' ? 'bg-green-500' :
                      sr.status === 'failed' ? 'bg-red-500' :
                      sr.status === 'skipped' ? 'bg-gray-400' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step results table */}
          {instance.stepResults.length > 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800">Résultats par étape</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {instance.stepResults.map((sr, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 px-4 py-3 ${stepStatusRowBg[sr.status]}`}
                  >
                    <div className="flex items-center gap-2 w-28 flex-shrink-0 mt-0.5">
                      {stepStatusIcon[sr.status]}
                      <span className="text-xs font-mono text-gray-500">Étape {sr.stepOrder}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-semibold ${
                        sr.status === 'passed' ? 'text-green-700' :
                        sr.status === 'failed' ? 'text-red-700' :
                        sr.status === 'skipped' ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {stepStatusLabel[sr.status]}
                      </span>
                      {sr.notes && (
                        <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{sr.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 px-4 py-8 text-center text-gray-400 text-sm">
              Aucun résultat d'étape enregistré.
            </div>
          )}
        </div>

        {/* Right — incidents + notes (1/3) */}
        <div className="space-y-4">

          {/* Incidents */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <h3 className="text-sm font-semibold text-gray-800">
                Incidents {instance.issues.length > 0 && `(${instance.issues.length})`}
              </h3>
            </div>
            {instance.issues.length > 0 ? (
              <div className="divide-y divide-orange-100">
                {instance.issues.map((issue, i) => (
                  <div key={i} className="px-4 py-3 bg-orange-50">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${severityColor[issue.severity]}`}>
                        {severityLabel[issue.severity]}
                      </span>
                      {issue.stepOrder != null && (
                        <span className="text-xs text-gray-500">Étape {issue.stepOrder}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-800">{issue.description}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {issue.jiraTicket && (
                        <span className="text-xs text-blue-600 font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                          Jira: {issue.jiraTicket}
                        </span>
                      )}
                      {issue.markerIoReportId && (
                        <span className="text-xs text-purple-600 font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-100">
                          Marker.io: {issue.markerIoReportId}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="px-4 py-4 text-xs text-gray-400 text-center">Aucun incident signalé</p>
            )}
          </div>

          {/* General notes */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">Notes générales</h3>
            </div>
            {instance.generalNotes ? (
              <p className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap">{instance.generalNotes}</p>
            ) : (
              <p className="px-4 py-4 text-xs text-gray-400 text-center">Aucune note</p>
            )}
          </div>

          {/* Linked test case card */}
          {tc && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800">Cas de test associé</h3>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs text-gray-400 font-mono mb-0.5">{tc.moduleId}</p>
                <p className="text-sm font-medium text-gray-800 mb-2">{tc.name}</p>
                {actorName && (
                  <p className="text-xs text-gray-500 mb-2">Acteur : {actorName}</p>
                )}
                <button
                  onClick={() => navigate(`/test-cases/${tc.id}`)}
                  className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                >
                  Voir le cas de test →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
